import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  LinearProgress,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import { AppPrimaryButtonOutlined } from 'shared/components/AppComponents';
import { ReactComponent as View } from 'assets/images/ic_view.svg';
import { ReactComponent as Maturity } from 'assets/images/ic_maturity.svg';
import { ReactComponent as Check } from 'assets/images/ic_check.svg';
import { ReactComponent as Growth } from 'assets/images/ic_growth.svg';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AIMaturityService } from 'services/AIMaturityService';
import { BROADCAST_MESSAGES } from 'shared/Constants';
import useBroadCast from 'shared/hooks/useBroadCast';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
// const LinearProgressWithLabel = (
//   props: LinearProgressProps & { value: number }
// ) => (
//   <Box sx={{ display: 'flex', alignItems: 'center' }}>
//     <Box sx={{ width: '100%', mr: 1 }}>
//       <LinearProgress variant="determinate" {...props} />
//     </Box>
//     <Box sx={{ minWidth: 35 }}>
//       <Typography
//         variant="body2"
//         sx={{ color: 'text.secondary' }}
//         // eslint-disable-next-line react/destructuring-assignment
//       >{`${Math.round(props.value)}%`}</Typography>
//     </Box>
//   </Box>
// );

export interface MaturitySummary {
  total_checklist: number;
  completed_count: number;
  percentage: number;
  next_stage_indicator: string;
  next_stage_name: string;
  status: string;
  isLoaded: boolean;
}

const AIMaturityAssessment = (
  props: AIMaturityAssessmentProps
): React.ReactElement => {
  const [maturitySummary, setMaturitySummary] = useState<MaturitySummary>({
    total_checklist: 0,
    completed_count: 0,
    percentage: 0,
    next_stage_indicator: '',
    next_stage_name: '',
    status: '',
    isLoaded: false,
  });
  const { minimize } = props;
  const navigation = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // API Calls
  const loadMaturitySummary = (): void => {
    setIsLoading(!maturitySummary.isLoaded);
    AIMaturityService.getSummary()
      .then((response) => {
        const { data: summary } = response.data;
        setMaturitySummary({ ...summary.overall, ...summary, isLoaded: true });
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Hooks
  useEffect(() => {
    loadMaturitySummary();
  }, []);

  useBroadCast({
    eventName: BROADCAST_MESSAGES.RELOAD_ASSESSMENTS,
    onBroadCast: () => {
      loadMaturitySummary();
    },
  });

  // UI Elements
  return (
    <Card variant="outlined">
      {minimize === false && (
        <CardHeader
          action={
            <AppPrimaryButtonOutlined
              startIcon={
                <SvgIcon
                  component={View}
                  inheritViewBox
                  className="view-icon"
                  fontSize="inherit"
                  htmlColor="#76A81F"
                />
              }
              title="View full assessment"
              onClick={() => navigation('/ai-maturity')}
            />
          }
          title="AI Maturity Assessment"
          // subheader="September 14, 2016"
        />
      )}
      {isLoading && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoading && (
        <>
          {' '}
          <CardContent>
            <Stack direction="column">
              <Stack direction="row">
                <Stack direction="row">
                  <SvgIcon
                    component={Maturity}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                    htmlColor="#052921"
                  />
                  <Typography variant="h2">AI Maturity Progress</Typography>
                </Stack>
                <Typography>{`${maturitySummary.percentage}%`}</Typography>
              </Stack>
              <Typography>Overal progress</Typography>
              <LinearProgress
                variant="determinate"
                className="my-3"
                value={maturitySummary.percentage}
              />
              <Typography variant="body1">
                {maturitySummary.completed_count} of{' '}
                {maturitySummary.total_checklist} items completed
              </Typography>
            </Stack>
            <Box>
              <Stack direction="row" spacing={1}>
                <SvgIcon
                  component={Check}
                  inheritViewBox
                  className="view-icon"
                  fontSize="inherit"
                />
                <Typography variant="h4">Current Stage</Typography>
              </Stack>
              <Typography variant="h3">
                {maturitySummary.next_stage_indicator}
              </Typography>
              <Typography>{maturitySummary.next_stage_name}</Typography>
            </Box>
            <Stack direction="row">
              <SvgIcon
                component={Growth}
                inheritViewBox
                className="view-icon"
                fontSize="inherit"
                htmlColor="#D1F27A"
              />
              <Typography>{maturitySummary.status}</Typography>
            </Stack>
          </CardContent>
          {minimize === false && (
            <CardActions disableSpacing>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Track your organistion’s AI governance and risk management
                progress.
              </Typography>
            </CardActions>
          )}
        </>
      )}
    </Card>
  );
};

export default AIMaturityAssessment;
